// app/components/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/PoonPhol.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

const CMS_KEYS = [
  "ourservices.poonphol.title",
  "ourservices.poonphol.content",
  "ourservices.poonphol.image1",
  "ourservices.poonphol.image2",
  "ourservices.poonphol.image3",
] as const;

type CmsKey = (typeof CMS_KEYS)[number];

export default function AtPoonPhol() {
  const t = useTranslations("OurServices");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // map pathname → CMS path (ตัด /en /zh ออกให้เหลือ path จริง)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---------- default map (i18n + static image src) ----------
  const buildDefaultMap = React.useCallback((): Record<CmsKey, string> => {
    return {
      "ourservices.poonphol.title": t("PoonPhol.title"),
      "ourservices.poonphol.content": t("PoonPhol.content"),
      "ourservices.poonphol.image1": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img1.png",
      "ourservices.poonphol.image2": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img2.png",
      "ourservices.poonphol.image3": "/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img3.png",
    };
  }, [t]);

  const [inline, setInline] = React.useState<Record<string, string>>(
    () => buildDefaultMap()
  );

  // reset เมื่อ locale เปลี่ยน
  React.useEffect(() => {
    setInline(buildDefaultMap());
  }, [locale, buildDefaultMap]);

  // //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          const key =
            e.cmsKey ||
            e.selector?.match(/data-cms-key="([^"]+)"/)?.[1] ||
            undefined;
          if (key) {
            map[key] = e.value;
          }
        });

        if (Object.keys(map).length > 0) {
          setInline((prev) => ({ ...prev, ...map }));
        }
      } catch (err) {
        console.warn("[OurServices PoonPhol] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  const txt = (key: CmsKey) => inline[key] ?? "";

  return (
    <>
      <Box className="ourteam-wrap our-services-ourteam-wrap" sx={{
        py: { xs: 5, sm: 10, lg: 10 }
      }}>
        <Box className="our-services-cloud"></Box>
        <Box className="">
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }} data-aos="fade-right" sx={{
              pr: {xs: 0, md: 3}
            }}>
              <Swiper
                spaceBetween={16}
                slidesPerView={1.2}
                dir="rtl"
                rewind={true}
                autoplay={{
                  delay: 5000,
                }}
                modules={[Autoplay]}
              >
                <SwiperSlide>
                  <Image
                    src="/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img1.png"
                    alt="our services poonphol 1"
                    title="our services poonphol 1"
                    width={678}
                    height={442}
                    className="img-fluid"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src="/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img2.png"
                    alt="our services poonphol 2"
                    title="our services poonphol 2"
                    width={678}
                    height={442}
                    className="img-fluid"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src="/assets/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/poonphol/img3.png"
                    alt="our services poonphol 3"
                    title="our services poonphol 3"
                    width={678}
                    height={442}
                    className="img-fluid"
                  />
                </SwiperSlide>
              </Swiper>
            </Grid>
          </Grid>
        </Box>

          {/* เนื้อหาฝั่งขวา */}
          <Box className="ourteam-content alignTop" sx={{
            pt: { xs: 5, md: 0 }
          }}>
            <Container className="container">
              <Grid container>
                <Grid
                  size={{ xs: 12, md: 6 }}
                  offset={{ xs: 0, md: 6 }}
                  sx={{
                    pl: { xs: 0, md: 3 }
                  }}
                >
                  <Typography
                    variant="h2"
                    data-cms-key="ourservices.poonphol.title"
                    sx={{
                      mb: 3,
                      whiteSpace: { xs: "normal", lg: "pre-line" }
                    }}>
                    {txt("ourservices.poonphol.title")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ textIndent: 2 }}
                    data-cms-key="ourservices.poonphol.content"
                  >
                    {txt("ourservices.poonphol.content")}
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
    </>
  );
}